import { useLoading } from '@genial/components';
import { Service } from '@genial/services';
import { AuthenticationResponsesValidateTfaToken } from '@genial/services/dist/models';
import { useSetRecoilState } from 'recoil';

import { useDownloadFileWithScreenLock } from '../../hooks/useDownloadFileWithScreenLock';
import { useToastCreator } from '../../hooks/useToastCreator';
import { addAuthorizationHeader } from '../../pages/BatchTicketInvestLink/Constants';
import { postTfaCodeService } from '../../pages/FixedFeeTermAcceptanceLink/services/authenticationService';
import { PostTfaToken } from '../../pages/FixedFeeTermAcceptanceLink/utils/types';

import { fixedFeePublicAuthorizationTokenState, fixedFeePublicTermIdState } from './atom';
import { screenName } from './constants';

export const useFixedFeeTermAcceptanceLinkActions = () => {
  const { show, hide } = useLoading();
  const { openFileAnotherTab } = useDownloadFileWithScreenLock();
  const { showToast } = useToastCreator('toast-fixed-fee-public-term-actions');
  const setTermId = useSetRecoilState(fixedFeePublicTermIdState);
  const setAuthorizationToken = useSetRecoilState(fixedFeePublicAuthorizationTokenState);

  const addTermId = (termId: string) => {
    setTermId(termId);
  };

  const postTfaCode = async ({
    tfaCode,
    termId,
  }: PostTfaToken): Promise<AuthenticationResponsesValidateTfaToken> => {
    try {
      const data = await postTfaCodeService(termId, tfaCode);

      setAuthorizationToken(data.tfaToken);

      return data.tfaToken;
    } catch (error) {
      showToast('error', 'Token invalido', 'Nao foi possível validar o token informado.');
      throw error;
    }
  };

  const acceptTerm = async ({ termId, jwtToken }: { termId: string; jwtToken?: string }) => {
    const errorTitle = 'Erro ao aceitar termo';
    const errorMessage = 'Tente novamente em alguns instantes.';
    try {
      show({ title: '', description: '' });
      const { data } = await Service.FixedFee.V1.postAgreementByCustomerAccepted(
        {
          screenName,
          fixedFeeAgreementId: termId,
        },
        addAuthorizationHeader(jwtToken)
      );
      if (data.success) {
        return;
      }
      showToast(
        'error',
        data.error.errorTitle || errorTitle,
        data.error.errorDisplay || errorMessage
      );
    } catch (error) {
      showToast('error', errorTitle, errorMessage);
      throw error;
    } finally {
      hide();
    }
  };

  const refuseTerm = async ({ termId, jwtToken }: { termId: string; jwtToken?: string }) => {
    const errorTitle = 'Erro ao recusar termo';
    const errorMessage = 'Tente novamente em alguns instantes.';
    try {
      show({ title: '', description: '' });
      const { data } = await Service.FixedFee.V1.postAgreementByCustomerCancel(
        {
          screenName,
          fixedFeeAgreementId: termId,
        },
        addAuthorizationHeader(jwtToken)
      );
      if (data.success) {
        return;
      }
      showToast(
        'error',
        data.error.errorTitle || errorTitle,
        data.error.errorDisplay || errorMessage
      );
    } catch (error) {
      showToast('error', errorTitle, errorMessage);
      throw error;
    } finally {
      hide();
    }
  };

  const downloadTerm = async ({ fileName, jwtToken }: { fileName: string; jwtToken?: string }) => {
    const data = await Service.FixedFee.V1.getAgreementFeeBasedContractTermFileFilter(
      {
        screenName,
        fileName,
      },
      addAuthorizationHeader(jwtToken)
    );
    if (data.data.url) {
      openFileAnotherTab(data.data.url);
      return;
    }
    showToast('error', 'Erro ao baixar termo', 'Tente novamente em alguns instantes.');
  };

  return {
    addTermId,
    postTfaCode,
    acceptTerm,
    refuseTerm,
    downloadTerm,
  };
};
